import {AfterViewChecked, Component, inject, OnDestroy, OnInit, ViewChild} from "@angular/core";
import {selectDealCompaniesForTreemap, selectDealCompaniesOverview, selectHasDealCompanies, selectSelectedDealId, selectShowDealGraphs,} from "../../store/deal/deal.selectors";
import {Store} from "@ngrx/store";
import {CodeTableEnum} from "../../../shared/model/code";
import {MatSort} from "@angular/material/sort";
import {map, Subscription} from "rxjs";
import {MatTableDataSource} from "@angular/material/table";
import {DealCompanyRecord} from "../../models/deal-company-record";
import {TraceableCode, TraceableDate, TraceableMoney, TraceablePercent, TraceableText} from "../../../shared/model/traceable";
import {MatPaginator} from "@angular/material/paginator";
import {TraceableFormatPipe} from "../../../shared/pipes/traceable-format/traceable-format.pipe";
import {DealCompanyActions} from "../../store/deal.actions";

@Component({
    selector: "valumize-deal-companies",
    templateUrl: "./deal-companies.component.html",
    styleUrls: ["./deal-companies.component.scss"],
    standalone: false
})
export class DealCompaniesComponent implements OnInit, OnDestroy, AfterViewChecked {
    private readonly store = inject(Store);
    private readonly traceableFormatPipe = inject(TraceableFormatPipe);

    subscription?: Subscription;

    dealId$ = this.store.select(selectSelectedDealId);
    dealCompany$ = this.store.select(selectDealCompaniesOverview);
    groupedDealCompanies$ = this.store.select(selectDealCompaniesForTreemap(this.traceableFormatPipe));
    showDealGraphs$ = this.store.select(selectShowDealGraphs);
    hasDealCompanies$ = this.store.select(selectHasDealCompanies);

    dealCompaniesDataSource: MatTableDataSource<DealCompanyRecord> = new MatTableDataSource();
    dealCompaniesColumns: string[] = [];
    codeTableCountry = CodeTableEnum.SHARED_COUNTRY;
    codeTableGpIndication = CodeTableEnum.ASSET_GPINDICATION;
    panelOpenState = true;
    treemapPanelOpenState = false;
    bubbleChartPanelOpenState = false;
    isSortApplied = false;
    @ViewChild("dealCompaniesSort") dealCompaniesSort = new MatSort();
    @ViewChild("dealCompaniesPaging") dealCompaniesPaging!: MatPaginator;

    constructor() {
    }

    ngOnInit() {
        this.subscription = this.store.select(selectDealCompaniesOverview).pipe(
            map(dealCompanies => {
                this.dealCompaniesDataSource = new MatTableDataSource<DealCompanyRecord>(dealCompanies.tableDatasource);
                this.dealCompaniesColumns = dealCompanies.tableColumns;

                this.dealCompaniesDataSource.paginator = this.dealCompaniesPaging;
                this.dealCompaniesDataSource.sort = this.dealCompaniesSort;
                this.dealCompaniesDataSource.sortingDataAccessor = (row: DealCompanyRecord, columnName: string): string | number => {
                    if (["fundName", "company"].includes(columnName)) {
                        return (row[columnName as keyof DealCompanyRecord] as TraceableText).text as string;
                    } else if (["country", "gpIndication"].includes(columnName)) {
                        return (row[columnName as keyof DealCompanyRecord] as TraceableCode).code as string;
                    } else if (["investmentDate", "exitDateAssumption"].includes(columnName)) {
                        return (row[columnName as keyof DealCompanyRecord] as TraceableDate).date as string;
                    } else if (["remainingCost", "gpNAV", "selectedBid", "cashflowLow", "cashflowBase", "cashflowHigh"].includes(columnName)) {
                        return (row[columnName as keyof DealCompanyRecord] as TraceableMoney).amount as number;
                    } else if (
                        ["percentageOfTotalNAV", "percentageOfTotalBid", "percentageOfTotalCfLow", "percentageOfTotalCfBase", "percentageOfTotalCfHigh"].includes(columnName)
                    ) {
                        return (row[columnName as keyof DealCompanyRecord] as TraceablePercent).fraction as number;
                    }
                    return row[columnName as keyof DealCompanyRecord] as string;
                };
            })
        ).subscribe();
    }

    ngAfterViewChecked(): void {
        if (!this.isSortApplied && this.dealCompaniesSort) {
            this.dealCompaniesDataSource.paginator = this.dealCompaniesPaging;
            this.dealCompaniesDataSource.sort = this.dealCompaniesSort;
            this.isSortApplied = true;
        }
    }

    loadCompaniesInDeal(dealId?: number | null) {
        if (!dealId) {
            return;
        }
        this.store.dispatch(DealCompanyActions.load({dealId}));
    }

    ngOnDestroy() {
        this.subscription?.unsubscribe();
    }
}
